import { Card } from '@/components/ui/card';
import { TrainingStats } from '@/hooks/useTrainingStats';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface TrainingCalendarProps {
  stats: TrainingStats;
}

export function TrainingCalendar({ stats }: TrainingCalendarProps) {
  const getIntensityClass = (attempts: number, successRate: number) => {
    if (attempts === 0) return 'bg-muted';
    if (successRate >= 75) {
      if (attempts >= 10) return 'bg-teen-green';
      if (attempts >= 5) return 'bg-teen-green/70';
      return 'bg-teen-green/40';
    } else if (successRate >= 60) {
      if (attempts >= 10) return 'bg-teen-gold';
      if (attempts >= 5) return 'bg-teen-gold/70';
      return 'bg-teen-gold/40';
    } else {
      if (attempts >= 10) return 'bg-teen-orange';
      if (attempts >= 5) return 'bg-teen-orange/70';
      return 'bg-teen-orange/40';
    }
  };

  // Get last 90 days
  const today = new Date();
  const days: Array<{ date: Date; attempts: number; correct: number; timeSpent: number }> = [];
  
  for (let i = 89; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split('T')[0];
    const activity = stats.dailyActivity.find(a => a.date === dateStr);
    
    days.push({
      date,
      attempts: activity?.attempts || 0,
      correct: activity?.correct || 0,
      timeSpent: activity?.timeSpent || 0
    });
  }

  // Group by weeks
  const weeks: Array<Array<typeof days[0]>> = [];
  let week: typeof days = [];
  
  days.forEach((day, idx) => {
    week.push(day);
    if (day.date.getDay() === 6 || idx === days.length - 1) {
      weeks.push([...week]);
      week = [];
    }
  });

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Training Activity</h3>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Less</span>
          <div className="w-3 h-3 bg-muted rounded-sm" />
          <div className="w-3 h-3 bg-teen-green/40 rounded-sm" />
          <div className="w-3 h-3 bg-teen-green/70 rounded-sm" />
          <div className="w-3 h-3 bg-teen-green rounded-sm" />
          <span className="text-muted-foreground">More</span>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <div className="inline-flex gap-1">
          {weeks.map((week, weekIdx) => (
            <div key={weekIdx} className="flex flex-col gap-1">
              {week.map((day, dayIdx) => {
                const successRate = day.attempts > 0 ? Math.round((day.correct / day.attempts) * 100) : 0;
                const intensityClass = getIntensityClass(day.attempts, successRate);
                
                return (
                  <TooltipProvider key={dayIdx}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div
                          className={`w-3 h-3 rounded-sm ${intensityClass} cursor-pointer hover:ring-2 hover:ring-primary transition-all`}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <div className="text-sm">
                          <p className="font-semibold">
                            {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </p>
                          {day.attempts > 0 ? (
                            <>
                              <p>{day.attempts} position{day.attempts !== 1 ? 's' : ''} attempted</p>
                              <p>{day.correct} correct ({successRate}%)</p>
                              <p>{Math.round(day.timeSpent / 60)} minutes</p>
                            </>
                          ) : (
                            <p className="text-muted-foreground">No training</p>
                          )}
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      
      <div className="mt-4 text-xs text-muted-foreground">
        Showing last 90 days of activity
      </div>
    </Card>
  );
}
